import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useFloating, flip, size, autoUpdate, useClick, useDismiss, useRole, useInteractions, FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import { cx } from 'class-variance-authority';
import { useNavigationContext } from '../NavigationContext/NavigationContext.js';
import { Overlay } from '../Overlay/Overlay.js';
import { zIndexes } from '../constants.js';

const UserMenuAvatar = ({ userNamePreview, }) => (jsx("span", { "aria-labelledby": "mci-menu-user-name", className: "mci-flex mci-size-[32px] mci-min-w-[32px] mci-items-center mci-justify-center mci-rounded-[50%] mci-border-1 mci-border-gray-300 mci-bg-gray-100 mci-text-body-xsmall mci-font-semibold", children: userNamePreview }));
const UserMenu = ({ children, className, isOpen, setIsOpen, userName, ...rest }) => {
    const { isExpanded } = useNavigationContext();
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        middleware: [
            isExpanded &&
                flip({
                    fallbackAxisSideDirection: "end",
                }),
            size({
                apply({ rects, elements }) {
                    Object.assign(elements.floating.style, {
                        padding: isExpanded ? `0 10px` : "0",
                        width: isExpanded ? `${rects.reference.width}px` : "224px",
                    });
                },
            }),
        ].filter(Boolean),
        onOpenChange: (value) => {
            setIsOpen(value);
        },
        placement: isExpanded ? "top-start" : "right-end",
        whileElementsMounted: autoUpdate,
    });
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        dismiss,
        role,
    ]);
    return (jsxs(Fragment, { children: [jsxs("button", { "aria-label": "Morning Consult user menu", className: cx("mci-flex mci-items-center mci-gap-s mci-px-s mci-py-3 mci-outline-brand mci-transition-colors mci-duration-fast hover:mci-bg-gray-200", className), ref: refs.setReference, ...rest, ...getReferenceProps(), children: [jsx(UserMenuAvatar, { userNamePreview: userName.slice(0, 1).toUpperCase() }), jsx("span", { className: cx("mci-text-body-small mci-font-semibold", !isExpanded && "mci-sr-only"), id: "mci-menu-user-name", children: userName })] }), isOpen && (jsx(FloatingPortal, { children: jsx(FloatingFocusManager, { context: context, modal: false, children: jsxs(Fragment, { children: [jsx(Overlay, { className: "mci-z-[320] md:mci-hidden", isVisible: isOpen }), jsx("div", { ref: refs.setFloating, style: { ...floatingStyles, zIndex: zIndexes.navigationDialog }, ...getFloatingProps(), children: jsx("ul", { className: "mci-relative mci-rounded-3 mci-border-gray-300 mci-bg-white mci-shadow-3", children: children }) })] }) }) }))] }));
};

export { UserMenu, UserMenuAvatar };
